import {
  Button,
  CircularProgress,
  Snackbar,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useI18n } from '../context/I18nContext';
import { useFormState } from '../hooks/useFormState';
import { useFormSubmission } from '../hooks/useFormSubmission';
import { useFormValidation } from '../hooks/useFormValidation';
import ErrorBoundary from './ErrorBoundary';
import FormField from './FormField';

const StyledForm = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--canvas-form-gap, 28px)',
  paddingTop: 'var(--canvas-form-label-gutter, 4px)',
  overflow: 'visible',
  '& .MuiFormControl-root': {
    marginTop: 0,
    overflow: 'visible',
  },
  // External labels above outlined controls (shared with canvas-ui.css)
  '& .MuiInputLabel-root': {
    position: 'relative !important',
    transform: 'none !important',
    maxWidth: 'none !important',
    overflow: 'visible',
    margin: '0 0 var(--canvas-label-gap, 8px)',
    padding: 0,
    lineHeight: 1.3,
    fontSize: '0.875rem !important',
    fontWeight: 500,
    letterSpacing: '0.01em',
  },
  '& .MuiOutlinedInput-notchedOutline legend': {
    display: 'none',
  },
  '& .MuiInputBase-root': {
    color: theme.palette.text.primary,
    backgroundColor: 'var(--canvas-field-bg, ' + theme.palette.background.paper + ')',
    borderRadius: 'var(--canvas-radius-sm, ' + theme.shape.borderRadius + 'px)',
    transition:
      'background-color 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
  },
  '& .MuiOutlinedInput-root': {
    '&:hover': {
      backgroundColor: 'var(--canvas-field-hover, rgba(26, 55, 240, 0.04))',
    },
    '&.Mui-focused': {
      boxShadow: '0 0 0 3px var(--canvas-focus-ring, rgba(26, 55, 240, 0.28))',
    },
    '&.Mui-error': {
      boxShadow:
        '0 0 0 3px color-mix(in srgb, var(--canvas-error, #c62828) 22%, transparent)',
    },
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 2,
    marginTop: theme.spacing(0.75),
    lineHeight: 1.35,
  },
  '& .MuiButton-containedPrimary': {
    alignSelf: 'flex-start',
    marginTop: theme.spacing(0.5),
    borderRadius: 'var(--canvas-radius-sm, ' + theme.shape.borderRadius + 'px)',
    boxShadow: 'none',
    transition:
      'transform 160ms ease, background-color 160ms ease, box-shadow 160ms ease',
    '&:hover': {
      boxShadow:
        '0 0 0 1px color-mix(in srgb, var(--canvas-primary, currentColor) 40%, transparent)',
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  },
}));

const JsonSchemaForm = forwardRef(
  (
    {
      schema,
      uiSchema = {},
      initialData = {},
      onSubmit,
      onChange,
      customComponents = {},
      customValidators = {},
      isLoading = false,
      theme,
      locale = 'en',
      hideSubmitButton = false,
    },
    ref
  ) => {
    const { t } = useI18n();
    const outerTheme = useTheme();
    const formTheme = useMemo(() => {
      if (theme == null || Object.keys(theme).length === 0) {
        return createTheme(outerTheme);
      }
      return createTheme(outerTheme, theme);
    }, [outerTheme, theme]);
    const memoizedSchema = useMemo(() => schema, [schema]);
    const memoizedInitialData = useMemo(() => initialData, [initialData]);
    const [isInitialized, setIsInitialized] = useState(false);

    const {
      formData,
      touched,
      handleChange,
      handleBlur,
      resetForm,
      setFormData,
    } = useFormState(memoizedInitialData, memoizedSchema);

    const { errors, isValid, validateAllFields, validateSingleField } =
      useFormValidation(memoizedSchema, formData, touched, customValidators);

    const { isSubmitting, snackbar, handleSubmit, closeSnackbar } =
      useFormSubmission(onSubmit, validateAllFields);

    useEffect(() => {
      if (!isInitialized) {
        setIsInitialized(true);
      }
    }, []);

    const memoizedHandleSubmit = useCallback(async () => {
      const validationResult = validateAllFields();
      if (validationResult.isValid) {
        try {
          if (typeof onSubmit !== 'function') {
            console.warn('onSubmit is not provided or is not a function');
            return true; // Return true to indicate successful validation
          }
          return await handleSubmit(formData);
        } catch (error) {
          console.error('Form submission error:', error);
          return false;
        }
      }
      return false;
    }, [handleSubmit, formData, validateAllFields, onSubmit]);

    const memoizedHandleChange = useCallback(
      (name, value) => {
        handleChange(name, value);
        if (onChange) {
          onChange({ ...formData, [name]: value });
        }
        validateSingleField(name, value);
      },
      [handleChange, onChange, formData, validateSingleField]
    );

    useImperativeHandle(
      ref,
      () => ({
        submit: memoizedHandleSubmit,
        validate: validateAllFields,
        getData: () => formData,
        reset: resetForm,
      }),
      [memoizedHandleSubmit, validateAllFields, formData, resetForm]
    );

    const requiredFields = useMemo(
      () => new Set(memoizedSchema?.required || []),
      [memoizedSchema]
    );

    const renderFields = useCallback(
      () =>
        memoizedSchema &&
        Object.entries(memoizedSchema.properties || {}).map(([key, fieldSchema]) => (
          <FormField
            key={key}
            name={key}
            schema={fieldSchema}
            uiSchema={uiSchema[key] || {}}
            value={formData[key]}
            onChange={memoizedHandleChange}
            onBlur={handleBlur}
            error={errors[key]}
            touched={touched[key]}
            required={requiredFields.has(key)}
            customComponents={customComponents}
          />
        )),
      [
        memoizedSchema,
        uiSchema,
        formData,
        memoizedHandleChange,
        handleBlur,
        errors,
        touched,
        requiredFields,
        customComponents,
      ]
    );

    const handleFormSubmit = useCallback(
      async (event) => {
        event.preventDefault();
        return await memoizedHandleSubmit();
      },
      [memoizedHandleSubmit]
    );

    if (!isInitialized) {
      return null;
    }

    return (
      <ErrorBoundary>
        <ThemeProvider theme={formTheme}>
          <StyledForm className="canvas-json-form" onSubmit={handleFormSubmit}>
            {renderFields()}
            {!hideSubmitButton && (
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting || isLoading || !isValid}
                startIcon={
                  isSubmitting || isLoading ? (
                    <CircularProgress size={20} />
                  ) : null
                }
                aria-busy={isSubmitting || isLoading}
              >
                {isSubmitting ? t('submitting') : t('submit')}
              </Button>
            )}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={6000}
              onClose={closeSnackbar}
              message={snackbar.message}
              severity={snackbar.severity}
            />
          </StyledForm>
        </ThemeProvider>
      </ErrorBoundary>
    );
  }
);

export default React.memo(JsonSchemaForm);
